import { FC, useState } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { useIntl } from 'dibs-react-intl';

import { sbRespRefineMenuFiltersMap } from '../../finding/SbRespRefineMenu/SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap';
import {
    VALID_DIMENSION_FILTERS,
    CATEGORY,
    SALE,
    PRICE,
} from '../../finding/SbSharedRefineMenu/sbSharedRefineMenuConstants';
import { BUY_PAGE_FILTERS } from '../../constants/buyConstants';

import { BuyRespTopNavFilters_itemSearch } from './__generated__/BuyRespTopNavFilters_itemSearch.graphql';
import { BuyRespTopNavFilters_user } from './__generated__/BuyRespTopNavFilters_user.graphql';
import { BuyRespTopNavFilters_viewer } from './__generated__/BuyRespTopNavFilters_viewer.graphql';
import { BuyRespRefineFilterDropdown } from './BuyRespRefineFilterDropdown';

import styles from '../../finding/SbRespRefineTopNav/SbRespRefineTopFilters.scss';

const FILTER_LIMIT = 9;

type Props = {
    itemSearch: BuyRespTopNavFilters_itemSearch;
    viewer: BuyRespTopNavFilters_viewer;
    user: BuyRespTopNavFilters_user;
    currency: string;
    isClient: boolean;
};

const BuyRespTopNavFiltersComponent: FC<Props> = ({
    itemSearch,
    viewer,
    user,
    currency,
    isClient,
}) => {
    const {
        filters,
        appliedFilters,
    }: {
        filters: BuyRespTopNavFilters_itemSearch['filters'];
        appliedFilters: BuyRespTopNavFilters_itemSearch['appliedFilters'];
    } = itemSearch;
    const [currentOpenFilterName, setCurrentOpenFilterName] = useState(''); // ensures only one filter open at a time
    const intl = useIntl();
    let dimensionsDisplayed = false;
    const appliedFilterNames = new Set(appliedFilters?.map(f => f?.name));
    if (appliedFilterNames.has(SALE)) {
        appliedFilterNames.add(PRICE); // sale filter is displayed under price facet
    }
    return (
        <div className={styles.container}>
            {filters
                ?.map((filter, idx) => {
                    if (!filter) {
                        return null;
                    }
                    const { values, totalCount, name, localizedFilterName } = filter;
                    if (!name || !BUY_PAGE_FILTERS.includes(name)) {
                        return null;
                    }
                    const { component: FilterComponent, title: unformattedIntlTitle } =
                        sbRespRefineMenuFiltersMap[name] || {};
                    const titleOverride =
                        !!unformattedIntlTitle && intl.formatMessage(unformattedIntlTitle);
                    const title = titleOverride || localizedFilterName;
                    if (!FilterComponent || !title) {
                        return null;
                    }
                    if (VALID_DIMENSION_FILTERS.includes(name)) {
                        if (dimensionsDisplayed) {
                            return null;
                        }
                        dimensionsDisplayed = true;
                    }
                    const isApplied = name.startsWith(CATEGORY)
                        ? false
                        : appliedFilterNames.has(name);
                    return (
                        <BuyRespRefineFilterDropdown
                            title={title}
                            isApplied={isApplied}
                            key={idx}
                            filterName={name}
                            currentOpenFilterName={currentOpenFilterName}
                            setCurrentOpenFilterName={setCurrentOpenFilterName}
                        >
                            <FilterComponent
                                itemSearch={itemSearch}
                                user={user}
                                viewer={viewer}
                                values={values}
                                filter={filter}
                                filters={filters}
                                currency={currency}
                                isClient={isClient}
                                filterName={name}
                                totalCount={totalCount}
                            />
                        </BuyRespRefineFilterDropdown>
                    );
                })
                .filter(Boolean)
                .slice(0, FILTER_LIMIT)}
        </div>
    );
};

export const BuyRespTopNavFilters = createFragmentContainer(BuyRespTopNavFiltersComponent, {
    itemSearch: graphql`
        fragment BuyRespTopNavFilters_itemSearch on ItemSearchQueryConnection {
            filters {
                name
                values {
                    displayName
                    urlLabel
                    count
                    code
                    linkReference
                    linkable
                    hexCode
                    stats {
                        key
                        values
                    }
                }
                totalCount
                localizedFilterName
            }
            appliedFilters {
                name
            }
            ...SbRespRefineMenuFilterPrice_itemSearch
            ...SbRespRefineMenuFilterDeliveryOptions_itemSearch
        }
    `,
    user: graphql`
        fragment BuyRespTopNavFilters_user on User {
            ...SbRespRefineMenuFilterPrice_user
            ...SbRespRefineMenuFilterDimensions_user
        }
    `,
    viewer: graphql`
        fragment BuyRespTopNavFilters_viewer on Viewer {
            ...SbRespRefineMenuFilterDeliveryOptions_viewer
        }
    `,
});
